// Frame-time governor + adaptive resolution for Cities of Bharat.
//
// Why this exists: the game must hold 60fps on integrated GPUs with 300+
// units. The cheapest GPU lever is pixelRatio (fill-rate + MSAA resolve
// cost scale ~quadratically), which can be changed at runtime — unlike
// MSAA itself. PerfMonitor tracks an EMA of frame dt and steps the render
// scale up/down so the game degrades gracefully instead of stuttering.

export type QualityLevel = 0 | 1 | 2 | 3; // 0=full, 3=potato

const SCALES = [1.5, 1.25, 1.0, 0.75];

export class PerfMonitor {
  private emaMs = 16.7;
  private cooldown = 0;
  private upStreak = 0;
  level: QualityLevel = 0;
  fps = 60;
  private baseCap: number;

  constructor() {
    // Respect the historic 1.5 cap, and low-DPR devices stay cheap.
    this.baseCap = Math.min(window.devicePixelRatio || 1, 1.5);
  }

  targetScale(): number {
    return Math.min(SCALES[this.level], this.baseCap);
  }

  get lowDetail(): boolean {
    return this.level >= 2;
  }

  get farHidden(): boolean {
    return this.level >= 3;
  }

  // Call once per rAF with clamped dt seconds. Returns the render scale to
  // use, or -1 when unchanged (caller can skip setPixelRatio).
  update(dt: number): number {
    const ms = dt * 1000;
    // EMA with fast attack on hitches, slow release so we don't flap.
    const k = ms > this.emaMs ? 0.25 : 0.06;
    this.emaMs += (ms - this.emaMs) * k;
    this.fps = 1000 / Math.max(this.emaMs, 0.01);
    this.cooldown -= dt;
    if (this.cooldown > 0) return -1;
    if (this.emaMs > 22 && this.level < 3) {
      // Sustained ~<45fps: step down immediately.
      this.level = (this.level + 1) as QualityLevel;
      this.cooldown = 2.0;
      this.upStreak = 0;
      return this.targetScale();
    }
    if (this.emaMs < 17.5 && this.level > 0) {
      // Sustained ~>57fps for a while: step back up.
      this.upStreak += 1;
      if (this.upStreak >= 4) {
        this.level = (this.level - 1) as QualityLevel;
        this.cooldown = 3.0;
        this.upStreak = 0;
        return this.targetScale();
      }
    } else {
      this.upStreak = 0;
    }
    return -1;
  }
}
